import { useCallback, useEffect, useMemo, useState } from "react"

import { finalize } from "rxjs/operators"

import { GetNotificationEventsObservable } from "./NotificationContactsProcess"
import NotificationEvent from "./NotificationEvent"
import handleAPIError from "../../../utils/handleAPIError"

/**
 * Custom hook to manage notification events.
 *
 * @param {NotificationEvent[]} selectedEvents - The initially selected notification events.
 * @returns {[boolean, Record<string, NotificationEvent>, React.Dispatch<React.SetStateAction<Record<string, NotificationEvent>>>, () => void]}
 *   An array containing:
 *   - loading: A boolean indicating if the data is being loaded.
 *   - events: A record of notification events.
 *   - setEvents: A function to update the events state.
 *   - resetEventsSelection: A function to reset the selection state of events.
 */
function useNotificationEvents(
  selectedEvents: NotificationEvent[] = []
): [
  loading: boolean,
  events: Record<string, NotificationEvent>,
  setEvents: React.Dispatch<React.SetStateAction<Record<string, NotificationEvent>>>,
  resetEventsSelection: () => void,
] {
  const [loading, setLoading] = useState(false)
  const [events, setEvents] = useState<Record<string, NotificationEvent>>({})

  useEffect(() => {
    setLoading(true)
    const subscription = GetNotificationEventsObservable()
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (data) => {
          setEvents(
            data.reduce<Record<string, NotificationEvent>>((acc, event) => {
              acc[event.Code] = {
                ...event,
                selected: selectedEvents.some(
                  (selected) => selected.RandomNotificationEventID === event.RandomNotificationEventID
                ),
              }
              return acc
            }, {})
          )
        },
        error: handleAPIError,
      })

    return () => subscription.unsubscribe()
  }, [selectedEvents])

  /**
   * Reset the selection state of all events to false.
   */
  const resetEventsSelection = useCallback(() => {
    setEvents((prev) => {
      const updatedEvents = Object.entries(prev).reduce<Record<string, NotificationEvent>>((acc, [key, event]) => {
        acc[key] = { ...event, selected: false }
        return acc
      }, {})
      return updatedEvents
    })
  }, [])

  return useMemo(
    () => [loading, events, setEvents, resetEventsSelection],
    [loading, events, setEvents, resetEventsSelection]
  )
}

export default useNotificationEvents
